import { ASSET_CLASS_IDS } from "~/consts";
import { AssetColumns } from "../assets/types";
import {
    ApiQuery,
    Filter,
    FilterOperators,
    SortDirections,
    SortQuery,
} from "../filters/types";
import { TokenTimeseriesColumns } from "../token_timeseries/types";
import { AssetClassColumns, AssetClassPage } from "./types";

// // // //

/**
 * DEFAULT_ASSET_CLASS_LIST_QUERY
 * Default ApiQuery for the /v4/asset_classes API
 */
export const DEFAULT_ASSET_CLASS_LIST_QUERY: ApiQuery = {
    sort: {
        direction: SortDirections.asc,
        field: AssetClassColumns.name,
    },
    pagination: {
        page: 1,
        perPage: 25,
    },
};

// // // //

// Map each asset class page to its SEO description
export const ASSET_CLASS_DESCRIPTIONS: Record<AssetClassPage, string> = {
    stablecoins:
        "Explore the activity and risks behind crypto and asset-backed stablecoins. View incumbent and new issuer on-chain traction, regulatory information, and structural data.",
    treasuries:
        "Explore tokenized U.S. government debt, including Treasury bills, notes, bonds, and Treasury-focused money market funds.",
    government_bonds:
        "Explore tokenized debt issued by non-U.S. governments and their agencies, including sovereign bonds, government-backed securities, and money market funds.",
    credit: "Explore tokenized credit assets, including all non-sovereign debt like private credit, onchain lending, corporate credit, structured and specialty credit, and more.",
    stocks: "Explore tokenized public equities, including listed stocks and ETFs, issued natively onchain or represented synthetically.",
    pe_vc: "Explore tokenized private equity and venture capital assets, including private market funds and equity in individual private companies.",
    active_strategies:
        "Explore tokenized assets providing exposure to active investment strategies, including hedge fund strategies, basis trades, and other dynamic approaches.",
    commodities:
        "Explore tokenized physical commodities, commodity-linked securities, and funds providing exposure to raw materials and natural resources.",
    real_estate:
        "Explore tokenized real estate, including direct ownership interests, funds, REITs, and real estate-backed debt.",
};

// Map each asset class page to Filter object
// TODO - rename to `ASSET_CLASS_PAGE_TO_ASSETS_FILTER`
export const ASSET_CLASS_PAGES_TO_FILTER: Record<AssetClassPage, Filter> = {
    stablecoins: {
        operator: FilterOperators.And,
        filters: [
            {
                field: AssetColumns.assetClassID,
                operator: FilterOperators.Equals,
                value: ASSET_CLASS_IDS.Stablecoin,
            },
            {
                field: AssetColumns.tokenCount,
                operator: FilterOperators.GreaterThan,
                value: 0,
            },
            {
                field: AssetColumns.bridgedTokenMarketCapDollar,
                operator: FilterOperators.GreaterThan,
                value: 0,
            },
        ],
    },
    treasuries: {
        operator: FilterOperators.And,
        filters: [
            {
                field: AssetColumns.assetClassID,
                operator: FilterOperators.Equals,
                value: ASSET_CLASS_IDS.TreasuryProduct,
            },
            {
                field: AssetColumns.isInvestable,
                operator: FilterOperators.Equals,
                value: true,
            },
        ],
    },
    government_bonds: {
        operator: FilterOperators.And,
        filters: [
            {
                field: AssetColumns.assetClassID,
                operator: FilterOperators.Equals,
                value: ASSET_CLASS_IDS.NonUSGovernmentDebt,
            },
            {
                field: AssetColumns.tokenCount,
                operator: FilterOperators.GreaterThan,
                value: 0,
            },
        ],
    },
    active_strategies: {
        operator: FilterOperators.And,
        filters: [
            {
                field: AssetColumns.assetClassID,
                operator: FilterOperators.Equals,
                value: ASSET_CLASS_IDS.ActiveStrategies,
            },
            {
                field: AssetColumns.tokenCount,
                operator: FilterOperators.GreaterThan,
                value: 0,
            },
        ],
    },
    commodities: {
        operator: FilterOperators.And,
        filters: [
            {
                field: AssetColumns.assetClassID,
                operator: FilterOperators.Equals,
                value: ASSET_CLASS_IDS.Commodities,
            },
            {
                field: AssetColumns.tokenCount,
                operator: FilterOperators.GreaterThan,
                value: 0,
            },
        ],
    },
    stocks: {
        operator: FilterOperators.And,
        filters: [
            {
                field: AssetColumns.assetClassID,
                operator: FilterOperators.Equals,
                value: ASSET_CLASS_IDS.Stocks,
            },
            {
                field: AssetColumns.tokenCount,
                operator: FilterOperators.GreaterThan,
                value: 0,
            },
        ],
    },
    pe_vc: {
        operator: FilterOperators.And,
        filters: [
            {
                operator: FilterOperators.Or,
                filters: [
                    {
                        field: AssetColumns.assetClassID,
                        operator: FilterOperators.Equals,
                        value: ASSET_CLASS_IDS.PrivateEquity,
                    },
                    {
                        field: AssetColumns.assetClassID,
                        operator: FilterOperators.Equals,
                        value: ASSET_CLASS_IDS.VentureCapital,
                    },
                ],
            },
            {
                field: AssetColumns.tokenCount,
                operator: FilterOperators.GreaterThan,
                value: 0,
            },
        ],
    },
    credit: {
        operator: FilterOperators.And,
        filters: [
            {
                operator: FilterOperators.Or,
                filters: [
                    {
                        field: AssetColumns.assetClassID,
                        operator: FilterOperators.Equals,
                        value: ASSET_CLASS_IDS.CorporateCredit,
                    },
                    {
                        field: AssetColumns.assetClassID,
                        operator: FilterOperators.Equals,
                        value: ASSET_CLASS_IDS.MunicipalBonds,
                    },
                    {
                        field: AssetColumns.assetClassID,
                        operator: FilterOperators.Equals,
                        value: ASSET_CLASS_IDS.DiversifiedCredit,
                    },
                    {
                        field: AssetColumns.assetClassID,
                        operator: FilterOperators.Equals,
                        value: ASSET_CLASS_IDS.AssetBackedCredit,
                    },
                    {
                        field: AssetColumns.assetClassID,
                        operator: FilterOperators.Equals,
                        value: ASSET_CLASS_IDS.SpecialtyFinance,
                    },
                ],
            },
            {
                field: AssetColumns.tokenCount,
                operator: FilterOperators.GreaterThan,
                value: 0,
            },
        ],
    },
    real_estate: {
        operator: FilterOperators.And,
        filters: [
            {
                field: AssetColumns.assetClassID,
                operator: FilterOperators.Equals,
                value: ASSET_CLASS_IDS.RealEstate,
            },
        ],
    },
};

// Map each asset class page to the Filter[] used to fetch its token timeseries data
export const ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS: Record<
    AssetClassPage,
    Filter[]
> = {
    stablecoins: [
        {
            field: TokenTimeseriesColumns.assetClassID,
            operator: FilterOperators.Equals,
            value: ASSET_CLASS_IDS.Stablecoin,
        },
    ],
    treasuries: [
        {
            field: TokenTimeseriesColumns.assetClassID,
            operator: FilterOperators.Equals,
            value: ASSET_CLASS_IDS.TreasuryProduct,
        },
        {
            field: TokenTimeseriesColumns.isInvestable,
            operator: FilterOperators.Equals,
            value: true,
        },
    ],
    government_bonds: [
        {
            field: TokenTimeseriesColumns.assetClassID,
            operator: FilterOperators.Equals,
            value: ASSET_CLASS_IDS.NonUSGovernmentDebt,
        },
    ],
    credit: [
        {
            operator: FilterOperators.Or,
            filters: [
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.Equals,
                    value: ASSET_CLASS_IDS.CorporateCredit,
                },
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.Equals,
                    value: ASSET_CLASS_IDS.MunicipalBonds,
                },
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.Equals,
                    value: ASSET_CLASS_IDS.DiversifiedCredit,
                },
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.Equals,
                    value: ASSET_CLASS_IDS.AssetBackedCredit,
                },
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.Equals,
                    value: ASSET_CLASS_IDS.SpecialtyFinance,
                },
            ],
        },
    ],
    stocks: [
        {
            field: TokenTimeseriesColumns.assetClassID,
            operator: FilterOperators.Equals,
            value: ASSET_CLASS_IDS.Stocks,
        },
    ],
    pe_vc: [
        {
            operator: FilterOperators.Or,
            filters: [
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.Equals,
                    value: ASSET_CLASS_IDS.PrivateEquity,
                },
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.Equals,
                    value: ASSET_CLASS_IDS.VentureCapital,
                },
            ],
        },
    ],
    active_strategies: [
        {
            field: TokenTimeseriesColumns.assetClassID,
            operator: FilterOperators.Equals,
            value: ASSET_CLASS_IDS.ActiveStrategies,
        },
    ],
    commodities: [
        {
            field: TokenTimeseriesColumns.assetClassID,
            operator: FilterOperators.Equals,
            value: ASSET_CLASS_IDS.Commodities,
        },
    ],
    real_estate: [
        {
            field: TokenTimeseriesColumns.assetClassID,
            operator: FilterOperators.Equals,
            value: ASSET_CLASS_IDS.RealEstate,
        },
    ],
};

export const ASSET_CLASS_PAGE_TO_SORT: Record<AssetClassPage, SortQuery> = {
    stablecoins: {
        field: AssetColumns.bridgedTokenMarketCapDollar,
        direction: SortDirections.desc,
    },
    treasuries: {
        field: AssetColumns.bridgedTokenValueDollar,
        direction: SortDirections.desc,
    },
    government_bonds: {
        field: AssetColumns.bridgedTokenValueDollar,
        direction: SortDirections.desc,
    },
    credit: {
        field: AssetColumns.bridgedTokenValueDollar,
        direction: SortDirections.desc,
    },
    stocks: {
        field: AssetColumns.bridgedTokenValueDollar,
        direction: SortDirections.desc,
    },
    pe_vc: {
        field: AssetColumns.bridgedTokenValueDollar,
        direction: SortDirections.desc,
    },
    active_strategies: {
        field: AssetColumns.bridgedTokenValueDollar,
        direction: SortDirections.desc,
    },
    commodities: {
        field: AssetColumns.bridgedTokenValueDollar,
        direction: SortDirections.desc,
    },
    real_estate: {
        field: AssetColumns.totalAssetValueDollar,
        direction: SortDirections.desc,
    },
};

// // // //

export const STABLECOIN_ASSETS_QUERY: ApiQuery = {
    filter: ASSET_CLASS_PAGES_TO_FILTER.stablecoins,
    sort: ASSET_CLASS_PAGE_TO_SORT.stablecoins,
    pagination: {
        page: 1,
        perPage: 25,
    },
};

export const TREASURY_ASSETS_QUERY: ApiQuery = {
    filter: ASSET_CLASS_PAGES_TO_FILTER.treasuries,
    sort: ASSET_CLASS_PAGE_TO_SORT.treasuries,
    pagination: {
        page: 1,
        perPage: 25,
    },
};

export const GOVERNMENT_BOND_ASSET_QUERY: ApiQuery = {
    filter: ASSET_CLASS_PAGES_TO_FILTER.government_bonds,
    sort: ASSET_CLASS_PAGE_TO_SORT.government_bonds,
    pagination: {
        page: 1,
        perPage: 25,
    },
};

// NOTE - this should be re-integrated into the Search module!
// This was removed from the search module because some ticker collisions between tokenized stocks and underlying stocks
// export const UNDERLYING_STOCK_ASSETS_QUERY: ApiQuery = {
//     filter: {
//         operator: FilterOperators.And,
//         filters: [
//             {
//                 field: AssetColumns.assetClassID,
//                 operator: FilterOperators.Equals,
//                 value: ASSET_CLASS_IDS.Stocks,
//             },
//             {
//                 field: AssetColumns.tokenCount,
//                 operator: FilterOperators.Equals,
//                 value: 0,
//             },
//         ],
//     },
//     sort: {
//         direction: SortDirections.desc,
//         field: AssetColumns.bridgedTokenValueDollar,
//     },
//     pagination: {
//         page: 1,
//         perPage: 2000,
//     },
// };
